import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Space,
  Spin,
  Tooltip,
  Divider,
  message
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import UserIcon from "../../../../assets/images/icons/userLogo.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../../../store";
import { TAB_KEYS } from "../../../../config";
import { useTab } from "../../../../custom-hooks/useTab";
import { useConnectsuForm } from "../../../../custom-hooks/useConnectsuForm";
import { getFormFieldValue } from "../../../../utils";
import ReactPlayer from "react-player/youtube";
import SynthesiaVideo from "./SynthesiaVideo";

const { TextArea } = Input;
const MAX_CHARACTERS = 700;

export default function Courses({ dataLoader, form }) {
  const { micrositeId } = useParams();
  const { state } = useLocation();
  const inputRef = useRef(null);
  const tabKey = state;
  const { tabData, courses_qualifications } = useStore();
  const whichTab = TAB_KEYS.courses;
  const { deleteSingleItem, postLoader } = useTab();
  const { updateImage } = useConnectsuForm();
  const [loader, setLoader] = useState(true);
  const [customName, setName] = useState("");
  const [items, setItems] = useState([]);
  let index = 0;
  const navigate = useNavigate();

  const [formChanged, setFormChanged] = useState(false);
  const { updatedValues } = useStore();

  const [canAddNew, setCanAddNew] = useState(true);
  const [hasUnsavedItem, setHasUnsavedItem] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // ✅ Empty Row Template
  const emptyRow = {
    id: undefined,
    assetTitleId: undefined,
    subTitle: "",
    description: "",
    urlLink: "",
    media: {},
    mediaLink: { mediaType: undefined, url: "" },
  };

  useEffect(() => {
    localStorage.setItem("tab", "COURSES");
    refreshUnsavedState();
  }, []);

  useEffect(() => {
    const isNotEmpty = Object.keys(updatedValues).length > 0;
    setFormChanged(isNotEmpty);
  }, [Object.keys(updatedValues).length]);

  // ✅ Refresh Unsaved State
  const refreshUnsavedState = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const unsavedItem = currentList.find(item => {
      const hasData =
        item?.subTitle?.trim() ||
        item?.description?.trim() ||
        item?.assetTitleId ||
        item?.urlLink?.trim() ||
        item?.mediaLink?.url?.trim() ||
        item?.mediaLink?.mediaType ||
        item?.media;
      return !item?.id && hasData;
    });

    setCanAddNew(!unsavedItem);
    setHasUnsavedItem(!!unsavedItem);
  };

  // ✅ Handle Add More - NEW ITEMS AT TOP
  const handleAddMore = () => {
    if (!canAddNew) {
      message.warning("You must save or clear the current item before adding a new one.");
      return;
    }

    const currentList = form.getFieldValue(whichTab) || [];
    const newList = [emptyRow, ...currentList]; // Add new row on top
    form.setFieldsValue({ [whichTab]: newList });

    setCanAddNew(false);
    setHasUnsavedItem(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ Handle Clear - CLEAR ONLY THE FIRST (NEWEST) UNSAVED ITEM
  const handleClear = () => {
    const currentList = form.getFieldValue(whichTab) || [];

    // Find the first unsaved item (index 0, since new items are added at top)
    const firstUnsavedIndex = currentList.findIndex(item => !item?.id);

    if (firstUnsavedIndex === -1) {
      message.warning("No unsaved item to clear.");
      return;
    }

    // Clear ONLY the first unsaved item
    const updatedList = currentList.map((item, idx) =>
      idx === firstUnsavedIndex
        ? {
          ...item,
          assetTitleId: undefined,
          subTitle: "",
          description: "",
          urlLink: "",
          media: {},
          mediaLink: { mediaType: undefined, url: "" },
        }
        : item
    );

    form.setFieldsValue({ [whichTab]: updatedList });

    // ✅ Recalculate unsaved state
    const stillUnsaved = updatedList.some(item =>
      !item?.id &&
      (
        item?.subTitle?.trim() ||
        item?.description?.trim() ||
        item?.assetTitleId ||
        item?.urlLink?.trim() ||
        item?.mediaLink?.url?.trim() ||
        item?.mediaLink?.mediaType
      )
    );

  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tabData?.courses?.length === 0) {
        setHasUnsavedItem(true);
      } else {
        setHasUnsavedItem(false);
      }
    }, 500);

    return () => clearTimeout(timer); // cleanup for safety
  }, [tabData?.courses]);

  const renderMedia = (dataItem) => {
    const commonStyles = {
      width: "350px",
      height: "250px",
      display: "flex",
      justifyContent: "center",
    };

    if (
      dataItem?.mediaLink?.mediaType === "SOCIAL_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <ReactPlayer
            controls={true}
            url={dataItem.mediaLink.url}
            width="100%"
            height="100%"
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "VIMEO_MEDIA" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <iframe
            src={dataItem.mediaLink.url}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            width="100%"
            height="100%"
            style={{ backgroundColor: "black" }}
          />
        </div>
      );
    } else if (
      dataItem?.mediaLink?.mediaType === "IMAGE" &&
      dataItem.mediaLink.url
    ) {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <img
            src={dataItem?.mediaLink?.url}
            alt=""
            className="c1 carousel-image"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </div>
      );
    }
    else if(
      dataItem?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
      dataItem.mediaLink.url) {
        return (
          <div>
      <SynthesiaVideo key={dataItem.id} videoData={dataItem} />

          </div>
                    )
      }
    return null;
  };

  const findValue = (i, key) => {
    return getFormFieldValue(form, whichTab, i, key);
  };

  const SelectChangeHandler = (e, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], "");
  };

  useEffect(() => {
    form.resetFields();
    refreshUnsavedState();
    setTimeout(() => {
      setLoader(false);
    }, 1000);
  }, [tabData[whichTab]]);

  const onNameChange = (event) => {
    setName([event.target.value]);
  };

  const addItem = (e) => {
    e.preventDefault();
    setItems([...items, customName[0] || `New item ${index++}`]);
    setName("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  useEffect(() => {
    if (items.length === 0) {
      tabData[whichTab] &&
        tabData[whichTab].map((item) => {
          if (item?.title && !items.includes(item.title)) {
            items.push(item?.title);
          }
        });
    }
  }, [tabData[whichTab]]);

  // ✅ Subscribe to form changes for unsaved state
  useEffect(() => {
    const unsubscribe = form.subscribe?.(() => refreshUnsavedState());
    return () => unsubscribe?.();
  }, [form]);

  if (dataLoader || postLoader || loader) {
    return (
      <div className="data-loader">
        <Spin tip="Loading" size="large" />
      </div>
    );
  } else {
    return (
      <div>
        <Row className="rowBody" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          {tabKey === whichTab ? (
            <>
              <Col className="gutter-row tabContent" span={24}>
                <Form.List
                  name={whichTab}
                  initialValue={
                    tabData && tabData[whichTab] && tabData[whichTab].length > 0
                      ? tabData[whichTab]
                      : [emptyRow]
                  }
                >
                  {(fields, { add, remove }) => (
                    <>
                      <div className="inputBody">
                        <label></label>
                        <div style={{ textAlign: "right" }}>
                          {hasUnsavedItem && (
                            <Button
                              className="cancelBtn"
                              type="button"
                              onClick={handleClear}
                            >
                              Clear
                            </Button>
                          )}
                          <Button className="greenBtn" onClick={handleAddMore}>
                            Add <PlusOutlined />
                          </Button>
                        </div>
                      </div>

                      {fields.map(({ key, name, ...restField }, i) => (
                        <div className="card-item" key={key}>
                          <Col
                            className="gutter-row tabContent"
                            span={16}
                            style={{ paddingRight: "25px" }}
                          >
                            <div className="d-flex">
                              <Form.Item
                                {...restField}
                                className="w-100"
                                label="Type of Qualification"
                                labelCol={{ span: 6 }}
                                name={[name, "assetTitleId"]}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      "Type of Qualification name is required",
                                  },
                                ]}
                              >
                                <Select
                                  options={courses_qualifications.map(
                                    (item) => ({
                                      label: item.title,
                                      value: item.id,
                                    })
                                  )}
                                  dropdownRender={(menu) => (
                                    <>
                                      {menu}
                                      <Divider style={{ margin: "8px 0" }} />
                                      <Space style={{ padding: "0 8px 4px" }}>
                                        <Button
                                          className="dropdow-configurations-btn greenBtn"
                                          type="text"
                                          icon={<PlusOutlined />}
                                          onClick={() => {
                                            localStorage.setItem(
                                              "page",
                                              "configurations"
                                            );
                                            navigate(
                                              "/micrositeConfigurations",
                                              {
                                                state: {
                                                  micrositeId: micrositeId,
                                                  page: "courses",
                                                },
                                              }
                                            );
                                          }}
                                        >
                                          Add course type
                                        </Button>
                                      </Space>
                                    </>
                                  )}
                                />
                              </Form.Item>
                              <Form.Item>
                                <div className="remove-btn">
                                  <DeleteOutlined
                                    title="Delete Course"
                                    onClick={() => {
                                      const id = form.getFieldValue([
                                        whichTab,
                                        i,
                                        "id",
                                      ]);
                                      if (id) {
                                        // Existing saved record → call delete API
                                        deleteSingleItem(whichTab, id, form);
                                      } else {
                                        // Newly added unsaved record → just remove from form list
                                        remove(name);
                                        // Refresh unsaved state after removal
                                        setHasUnsavedItem(false);
                                        refreshUnsavedState();
                                      }
                                    }}
                                  />
                                </div>
                              </Form.Item>
                            </div>

                            <Form.Item
                              {...restField}
                              className="w-100"
                              label="Course"
                              labelCol={{ span: 6 }}
                              name={[name, "subTitle"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Course is required",
                                },
                              ]}
                            >
                              <Input placeholder="Course" />
                            </Form.Item>

                            <Form.Item
                              label="Description"
                              {...restField}
                              labelCol={{ span: 6 }}
                              name={[name, "description"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Description is required",
                                },
                                {
                                  max: MAX_CHARACTERS - 1,
                                  message: `Description should be less than ${MAX_CHARACTERS} characters`,
                                },
                              ]}
                            >
                              <TextArea
                                placeholder="Enter Description"
                                rows={5}
                                maxLength={MAX_CHARACTERS}
                                showCount
                              />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              className="w-100"
                              label="Course Link"
                              labelCol={{ span: 6 }}
                              name={[name, "urlLink"]}
                            >
                              <Input placeholder="Course Link" />
                            </Form.Item>

                            <div className="d-flex">
                              <Form.Item
                                {...restField}
                                className="w-100"
                                name={[name, "mediaLink", "mediaType"]}
                                label="Video or Image"
                                labelCol={{ span: 6 }}
                              >
                                <Select placeholder="Select Option" allowClear>
                                  <Select.Option value="VIMEO_MEDIA">
                                    Add Vimeo Link
                                  </Select.Option>
                                  <Select.Option value="SOCIAL_MEDIA">
                                    Add Youtube Link
                                  </Select.Option>
                                  <Select.Option value="SYNTHESIA_MEDIA">
                                    Add Synthesia Link
                                  </Select.Option>
                                  <Select.Option value="IMAGE">
                                    Upload Image
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                              <Form.Item>
                                <div className="remove-btn">
                                  <Tooltip title="Upload Video link or image with size lesser than 2.5MB. For best results, use images and videos with a 16:9 aspect ratio">
                                    <QuestionCircleOutlined />
                                  </Tooltip>
                                </div>
                              </Form.Item>
                            </div>

                            {findValue(i, "type") === "SOCIAL_MEDIA" ? (
                              <Form.Item
                                label="Youtube Link"
                                {...restField}
                                labelCol={{ span: 6 }}
                                name={[name, "mediaLink", "url"]}
                              >
                                <Input />
                              </Form.Item>
                            ) : findValue(i, "type") === "VIMEO_MEDIA" ? (
                              <Form.Item
                                label="Vimeo Link"
                                labelCol={{ span: 6 }}
                                {...restField}
                                name={[name, "mediaLink", "url"]}
                              >
                                <Input />
                              </Form.Item>
                            ) : findValue(i, "type") === "IMAGE" ? (
                              <Form.Item
                                {...restField}
                                label="Image"
                                colon={false}
                                name="media"
                                className="experienceImg"
                                labelCol={{ span: 6 }}
                              >
                                {findValue(i, "blob") ? (
                                  <Avatar
                                    src={URL.createObjectURL(
                                      findValue(i, "blob")
                                    )}
                                    alt=""
                                  />
                                ) : findValue(i, "url") ? (
                                  <Avatar src={findValue(i, "url")} alt="" />
                                ) : (
                                  <Avatar src={UserIcon} />
                                )}
                                <label
                                  name="file"
                                  className="fileUploadBtn greenBtn"
                                >
                                  Choose File
                                  <input
                                    accept="image/png, image/gif, image/jpeg"
                                    type="file"
                                    onChange={(e) => {
                                      updateImage(e, i, form, whichTab);
                                      // 🔁 Force re-render after image upload
                                      setTimeout(
                                        () => setRefreshKey((prev) => prev + 1),
                                        200
                                      );
                                    }}
                                  />
                                </label>
                              </Form.Item>
                            ) : findValue(i, "type") === "SYNTHESIA_MEDIA" ? (
                              <Form.Item
                                label="Synthesia Link"
                                labelCol={{ span: 6 }}
                                {...restField}
                                name={[name, "mediaLink", "url"]}
                              
                              >
                                <Input />
                              </Form.Item>
                            ) : null}

                            <Form.Item
                              className="w-100"
                              style={{ display: "flex", justifyContent: "end" }}
                            >
                              {formChanged ? (
                                <Button
                                  className="saveBtn greenBtn"
                                  onClick={() => {
                                    form.submit();
                                    setCanAddNew(true);
                                    setHasUnsavedItem(false);
                                  }}
                                >
                                  Save
                                </Button>
                              ) : (
                                <Button
                                  className="saveBtn greenBtn"
                                  style={{
                                    opacity: "0.5",
                                    pointerEvents: "none",
                                  }}
                                >
                                  Save
                                </Button>
                              )}
                            </Form.Item>
                          </Col>
                          <Col className="gutter-row tabContent" span={8}>
                            {form.getFieldValue(whichTab)?.[i] ? (
                              <div key={refreshKey}>
                                {renderMedia(form.getFieldValue(whichTab)[i])}
                              </div>
                            ) : null}
                          </Col>
                        </div>
                      ))}
                    </>
                  )}
                </Form.List>
              </Col>
            </>
          ) : (
            <></>
          )}
        </Row>
      </div>
    );
  }
}